import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLoadStages } from '@/shell/useLoadStages';
import { STALLED_LOAD_FAILURE } from '@/shell/useLoadDeadline';
import { LoadFailureBanner } from '@/components/LoadFailureBanner';
import { Waiting } from './Waiting';

/** The study card's furthest offset from the top of its placeholder (WAIT-21). */
export const STUDY_CARD_TOP_PX = 240;

export type StudyPlacementType = { fit: 'card'; top: number } | { fit: 'line' };

/**
 * WAIT-21b (I-1867): where the study stands in a placeholder `boxHeight` tall, its card `cardHeight`
 * tall. The card at most 15 rem down and never past the bottom; a box too short for the card holds the
 * study as one line. Before anything is measured (0 and 0) the card stands at the top.
 */
export function studyPlacement(boxHeight: number, cardHeight: number): StudyPlacementType {
  if (cardHeight > boxHeight) return { fit: 'line' };
  return { fit: 'card', top: Math.min(STUDY_CARD_TOP_PX, boxHeight - cardHeight) };
}

/**
 * F-0604 WAIT-19 to WAIT-22 (`docs/concept-waiting-motion.md`): what a pane or page draws while it
 * waits, by the one set of stages (`useLoadStages`).
 *
 * `placeholder` is the layout the content becomes, drawn from the first frame with whatever the
 * opener already knows in it as real words (WAIT-19); its bars are the live skeleton, which shows only
 * after 300 ms (the class's own hold, `index.css`, so nothing moves when they arrive, WAIT-20).
 * From 2 s the session's study with `caption` stands over it (WAIT-21), inside the placeholder's own
 * box: as a card where the box holds one, as one line where it does not (WAIT-21b). From the
 * cold-stall deadline the placeholder is replaced by the shared failure with *Try again* (WAIT-22).
 */
export function LoadStage({
  waiting,
  stageKey,
  caption,
  failureTitle,
  onRetry,
  placeholder,
  deadline = true,
}: {
  waiting: boolean;
  /** What is loading: a change of it starts the stages again. */
  stageKey: string;
  /** *Loading D-0180…* */
  caption: string;
  /** *This decision couldn't be loaded* */
  failureTitle: string;
  onRetry: () => void;
  placeholder: ReactNode;
  /** `false` where the screen's own deadline owns the failure (`useStalledLoad`). */
  deadline?: boolean;
}) {
  // *Try again* starts the stages again over the new read (RD-35: a deadline over for one read is not
  // over for the next).
  const [attempt, setAttempt] = useState(0);
  const stage = useLoadStages(waiting, `${stageKey}:${attempt}`, deadline);
  const retry = useCallback(() => {
    setAttempt((count) => count + 1);
    onRetry();
  }, [onRetry]);
  const boxRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<StudyPlacementType | null>(null);
  // Measured before the study is painted, so it is never drawn where it does not fit.
  useLayoutEffect(() => {
    if (stage !== 'study') return setPlacement(null);
    if (placement) return undefined;
    setPlacement(
      studyPlacement(boxRef.current?.offsetHeight ?? 0, cardRef.current?.offsetHeight ?? 0),
    );
    return undefined;
  }, [stage, placement]);
  if (stage === 'failed') {
    return (
      <LoadFailureBanner
        title={failureTitle}
        failure={STALLED_LOAD_FAILURE}
        retrying={false}
        onRetry={retry}
        testId="load-stage-failure"
      />
    );
  }
  return (
    <div ref={boxRef} data-load-stage={stage} aria-busy="true" className="relative">
      {placeholder}
      {stage === 'study' && placement?.fit === 'line' ? (
        <div
          data-load-stage-study="line"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-start"
        >
          {/* One line over its bars, in a placeholder too short for the card (WAIT-21b). */}
          <div className="flex max-w-full items-center gap-2 rounded-full bg-white/90 px-3 py-1 shadow-sm ring-1 ring-black/5 dark:bg-[#171717]/90 dark:ring-white/10">
            <Waiting size="inline" silent />
            <p className="min-w-0 truncate text-[12px] leading-4 text-gray-500 dark:text-gray-400">
              {caption}
            </p>
          </div>
        </div>
      ) : stage === 'study' ? (
        <div
          data-load-stage-study="card"
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 flex justify-center ${placement ? '' : 'invisible'}`}
          style={{ top: placement?.fit === 'card' ? placement.top : 0 }}
        >
          {/* A card, so the study and its words read over the bars under them in both themes. */}
          <div
            ref={cardRef}
            className="flex flex-col items-center gap-2 rounded-xl bg-white/90 px-6 py-4 text-center shadow-sm ring-1 ring-black/5 dark:bg-[#171717]/90 dark:ring-white/10"
          >
            <Waiting size="section" silent />
            <p className="text-[13px] leading-5 text-gray-500 dark:text-gray-400">{caption}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * F-0604 WAIT-21: the session's study with its caption over a list that has its own skeleton and its own
 * failure (the Items list). From 2 s of a wait it stands in a card in the middle of the window, outside
 * the page's own element tree (a portal), so it moves nothing in the list and the list's own placeholder
 * logic never sees it. It leaves when the wait ends.
 */
export function LoadStudy({
  waiting,
  stageKey,
  caption,
}: {
  waiting: boolean;
  stageKey: string;
  caption: string;
}) {
  const stage = useLoadStages(waiting, stageKey, false);
  if (stage !== 'study' || typeof document === 'undefined') return null;
  return createPortal(
    <div
      data-load-stage-study=""
      aria-hidden="true"
      className="pointer-events-none fixed left-1/2 top-1/3 z-30 -translate-x-1/2"
    >
      <div className="flex flex-col items-center gap-2 rounded-xl bg-white/90 px-6 py-4 text-center shadow-sm ring-1 ring-black/5 dark:bg-[#171717]/90 dark:ring-white/10">
        <Waiting size="section" silent />
        <p className="text-[13px] leading-5 text-gray-500 dark:text-gray-400">{caption}</p>
      </div>
    </div>,
    document.body,
  );
}
